import {
  type Ref,
  type ReactElement,
  createContext,
  useContext,
  cloneElement,
} from 'react'
import { clamp } from 'lodash-es'
import { IsSkeletonContext } from '@/components/Skeleton/index'

export interface SkeletonListContextProps {
  /**
   * Returns a percentage value that will be close to the input but vary
   * between each instance of the item.
   *
   * @param value The specified value to vary.
   * @param multipliers Custom multipliers to use.
   */
  varyPercent: (value: number, multipliers?: number[]) => string
  /**
   * Returns a value that will be close to the input but vary
   * between each instance of the item.
   *
   * @param value The specified value to vary.
   * @param multipliers Custom multipliers to use.
   */
  vary: (value: number, multipliers?: number[]) => number
  /**
   * If specified on the {@SkeletonList}, this ref will be available
   * to the _first_ element rendered by the list via the context.
   */
  ref?: Ref<Element>
  /**
   * The index of the item being rendered.
   */
  index: number
}

/**
 * Context for the skeleton list item.
 */
export const SkeletonListItemContext = createContext<SkeletonListContextProps>({
  // Default value doesn't apply any variability.
  varyPercent: (value) => `${value}%`,
  vary: (value) => value,
  index: 0,
})

/**
 * Children prop of {@link SkeletonList}.
 */
export type SkeletonListChildren =
  | ReactElement
  | ((ctx: SkeletonListContextProps) => ReactElement)

export interface SkeletonListProps {
  /**
   * How many items to render.
   */
  count?: number

  /**
   * The element to clone.
   */
  children: SkeletonListChildren

  /**
   * If specified, the ref will be available to the first or last element
   * rendered by the list via the context, depending on {@link attachRefTo} value
   */
  ref?: Ref<Element>

  /**
   * On which element to assign the ref, the first, the last, or every element.
   *
   * @default 'first'
   */
  attachRefTo?: 'first' | 'last' | 'all'
}

/**
 * Gets the skeleton list context.
 */
export function useSkeletonListContext(): SkeletonListContextProps {
  return useContext(SkeletonListItemContext)
}

const MULTIPLIERS = [1, 0.9, 1.1, 1.2, 0.85, 1.05, 1.2, 1, 1.25]

/**
 * Takes a single component as a child which will be cloned
 * as many times as specified and will have a context available
 * to make each item render differently so each skeleton in the list
 * doesn't look the same.
 */
export default function SkeletonList({
  count = 3,
  children,
  ref,
  attachRefTo = 'first',
}: SkeletonListProps) {
  const cloned = new Array(count)
  const inner =
    typeof children === 'function' ? (
      <SkeletonListItemContext.Consumer>
        {children}
      </SkeletonListItemContext.Consumer>
    ) : (
      children
    )

  for (let i = 0; i < count; i++) {
    // eslint-disable-next-line react-compiler/react-compiler
    const contextValue = createSkeletonListItem({
      index: i,
      count,
      ref,
      attachRefTo,
    })
    cloned[i] = (
      <SkeletonListItemContext key={`skeleton-${i}`} value={contextValue}>
        {cloneElement(inner)}
      </SkeletonListItemContext>
    )
  }
  return (
    <IsSkeletonContext.Provider value={true}>
      {cloned}
    </IsSkeletonContext.Provider>
  )
}

interface SkeletonListItem {
  /**
   * Index for the skeleton item in the overall list of skeletons.
   */
  index: number
  /**
   * Ref to attach to the item based on the {@link attachRefTo} value.
   */
  ref?: Ref<Element>
  /**
   * Where to attach the ref, either the first or last item.
   */
  attachRefTo?: SkeletonListProps['attachRefTo']
  /**
   * The total count of skeleton items, used to know which element to attach
   * the ref to, as well as adjusting what each of the `vary` methods return.
   */
  count: number
}

/**
 * Creates a context value for use with {@link SkeletonListItemContext}.
 */
export function createSkeletonListItem({
  index,
  ref,
  attachRefTo,
  count,
}: SkeletonListItem): SkeletonListContextProps {
  return {
    index: index,
    varyPercent: (value, multipliers = MULTIPLIERS) => {
      const multiplier = multipliers[index % (multipliers.length - 1)]
      return `${clamp(value * multiplier, 0, 100)}%`
    },
    vary: (value, multipliers = MULTIPLIERS) => {
      const multiplier = multipliers[index % (multipliers.length - 1)]
      return value * multiplier
    },
    ref:
      index === 0 && attachRefTo === 'first'
        ? ref
        : index === count - 1 && attachRefTo === 'last'
          ? ref
          : attachRefTo === 'all'
            ? ref
            : null,
  }
}
